import React, { memo } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
} from '@/components/modal';
import { Button } from '@/components/button/Buttons';
import { KeenIcon } from '@/components';
import { IRole } from '@/types/roles-permissions';

interface DeleteRoleModalProps {
  isOpen: boolean;
  isDeleting: boolean;
  role: IRole | null;
  onClose: () => void;
  onConfirmDelete: () => void;
}

const LoadingSpinner = () => (
  <svg
    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    ></circle>
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    ></path>
  </svg>
);

const DeleteRoleModal: React.FC<DeleteRoleModalProps> = ({
  isOpen,
  isDeleting,
  role,
  onClose,
  onConfirmDelete,
}) => {
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !isDeleting) {
      onConfirmDelete();
    }
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <ModalContent className="max-w-md mx-auto mt-20">
        <ModalHeader>
          <ModalTitle>Delete Role</ModalTitle>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            type="button"
            aria-label="Close"
            disabled={isDeleting}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </ModalHeader>
        <ModalBody>
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-800 contrast:bg-red-800 mb-4">
              <KeenIcon type="filled" icon="trash" className="text-red-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Delete Role
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              Are you sure you want to delete role "
              <span className="font-semibold">{role?.display_name}</span>"? This
              action cannot be undone.
            </p>
            <div className="flex justify-center space-x-3 mt-6">
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={isDeleting}
                aria-label="Cancel deletion"
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="danger"
                onClick={onConfirmDelete}
                disabled={isDeleting}
                onKeyDown={handleKeyDown}
                aria-label="Confirm delete role"
              >
                {isDeleting ? (
                  <>
                    <LoadingSpinner />
                    Deleting...
                  </>
                ) : (
                  'Delete Role'
                )}
              </Button>
            </div>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(DeleteRoleModal);
